import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { PlannerSourceSettingsApi } from "./PlannerSourceSettings.Api";
import type { PlannerSourceSettingsViewModel } from "./PlannerSourceSettings.ViewModel.types";
import type {
  PlannerBucket,
  PlannerConfiguration,
  PlannerMode,
} from "./PlannerSourceSettings.types";

const NONE_OPTION = "__none";

const isBucketIncluded = (
  buckets: PlannerBucket[] | undefined,
  bucketId: string,
): boolean => {
  return Boolean(buckets?.some((bucket) => bucket.id === bucketId));
};

export const usePlannerSourceSettingsViewModel = (
  model: SourceSettingsModel,
): PlannerSourceSettingsViewModel => {
  const [plans, setPlans] = useState<PlannerConfiguration["plan"][]>([]);
  const [buckets, setBuckets] = useState<PlannerBucket[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingBuckets, setIsLoadingBuckets] = useState(false);
  const [hasError, setHasError] = useState(false);
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const modeInitialisedConnectionIdRef = useRef<string | null>(null);
  const seededPlanIdRef = useRef<string | null>(null);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.MicrosoftPlanner && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const mode =
    configuration.mode === "GroupPlanTasks"
      ? "PlanTasks"
      : (configuration.mode ?? "MyTasks");
  const isPlanMode = mode === "PlanTasks";

  useEffect(() => {
    if (!isVisible) {
      modeInitialisedConnectionIdRef.current = null;
      return;
    }

    if (configuration.mode === "GroupPlanTasks") {
      updateConnectionConfiguration<PlannerConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) =>
          cfg.mode === "GroupPlanTasks" ? { ...cfg, mode: "PlanTasks" } : cfg,
        { isDefault: true },
      );
      return;
    }

    if (modeInitialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    modeInitialisedConnectionIdRef.current = editingConnectionId;

    if (configuration.mode && configuration.assignedToMe !== undefined) {
      return;
    }

    // The editor shows My Tasks, assigned to me, when neither is stored.
    updateConnectionConfiguration<PlannerConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        mode: cfg.mode ?? "MyTasks",
        assignedToMe: cfg.assignedToMe ?? true,
      }),
      { isDefault: true },
    );
  }, [configuration.assignedToMe, configuration.mode, editingConnectionId, isVisible]);

  useEffect(() => {
    const loadPlans = async (): Promise<void> => {
      if (!isVisible || !isPlanMode || !model.boardId || !editingConnectionId) {
        setPlans([]);
        return;
      }

      const plannerApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<PlannerSourceSettingsApi>(
          SourceTypeIds.MicrosoftPlanner,
        );
      if (!plannerApi) {
        setPlans([]);
        return;
      }

      setIsLoadingPlans(true);
      setHasError(false);
      try {
        setPlans(await plannerApi.getPlans(model.boardId, editingConnectionId));
      } catch {
        setPlans([]);
        setHasError(true);
      } finally {
        setIsLoadingPlans(false);
      }
    };

    void loadPlans();
  }, [isPlanMode, isVisible, model.boardId, editingConnectionId]);

  useEffect(() => {
    const loadBuckets = async (): Promise<void> => {
      if (
        !isVisible ||
        !isPlanMode ||
        !model.boardId ||
        !editingConnectionId ||
        !configuration.plan?.id
      ) {
        setBuckets([]);
        return;
      }

      const plannerApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<PlannerSourceSettingsApi>(
          SourceTypeIds.MicrosoftPlanner,
        );
      if (!plannerApi) {
        setBuckets([]);
        return;
      }

      setIsLoadingBuckets(true);
      setHasError(false);
      try {
        const nextBuckets = await plannerApi.getPlanBuckets(
          model.boardId,
          editingConnectionId,
          configuration.plan.id,
        );

        setBuckets(nextBuckets);

        const shouldSeedDefaults =
          (configuration.buckets?.length ?? 0) === 0 &&
          nextBuckets.length > 0 &&
          seededPlanIdRef.current !== configuration.plan.id;

        if (shouldSeedDefaults) {
          seededPlanIdRef.current = configuration.plan.id;
          const initialBuckets = nextBuckets.slice(
            0,
            nextBuckets.length > 1 ? nextBuckets.length - 1 : 0,
          );
          const initialDoneBucket =
            nextBuckets.length > 1
              ? nextBuckets[nextBuckets.length - 1]
              : undefined;

          const seededPlanId = configuration.plan.id;
          // Seeds only the plan the buckets were loaded for, and only what is
          // missing, so it is safe to apply to the saved settings too.
          updateConnectionConfiguration<PlannerConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              cfg.plan?.id !== seededPlanId || (cfg.buckets?.length ?? 0) > 0
                ? cfg
                : {
                    ...cfg,
                    buckets: initialBuckets,
                    doneBucket: cfg.doneBucket ?? initialDoneBucket,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setBuckets([]);
        setHasError(true);
      } finally {
        setIsLoadingBuckets(false);
      }
    };

    void loadBuckets();
  }, [
    configuration.buckets?.length,
    configuration.plan?.id,
    isPlanMode,
    isVisible,
    model.boardId,
    editingConnectionId,
  ]);

  const selectedPlanId = configuration.plan?.id ?? NONE_OPTION;
  const selectedBucketIds = (configuration.buckets ?? []).map(
    (bucket) => bucket.id,
  );
  const selectedDoneBucketId = configuration.doneBucket?.id ?? NONE_OPTION;

  const bucketReferenceErrorText = useMemo(() => {
    if (
      !isPlanMode ||
      selectedDoneBucketId === NONE_OPTION ||
      !isBucketIncluded(configuration.buckets, selectedDoneBucketId)
    ) {
      return "";
    }

    return model.t(
      "board.connections.dialog.sourceSettings.planner.bucketReferenceError",
    );
  }, [configuration.buckets, isPlanMode, model, selectedDoneBucketId]);

  return {
    isVisible,
    mode,
    plans: plans.filter((plan): plan is NonNullable<typeof plan> =>
      Boolean(plan),
    ),
    buckets,
    selectedPlanId,
    selectedPlanTitle:
      selectedPlanId === NONE_OPTION
        ? ""
        : plans.find((plan) => plan?.id === selectedPlanId)?.title || "",
    selectedBucketIds,
    selectedDoneBucketId,
    selectedDoneBucketName:
      selectedDoneBucketId === NONE_OPTION
        ? ""
        : buckets.find((bucket) => bucket.id === selectedDoneBucketId)?.name ||
          "",
    noneOptionValue: NONE_OPTION,
    isLoadingPlans,
    isLoadingBuckets,
    hasError,
    bucketReferenceErrorText,
    isPlanMode,
    showCompleteWhenDone: selectedDoneBucketId !== NONE_OPTION,
    assignedToMe: configuration.assignedToMe ?? true,
    completeWhenDone: Boolean(configuration.completeWhenDone),
    onSelectMode: (nextMode: PlannerMode) => {
      updateConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => ({
          ...cfg,
          mode: nextMode,
          plan: undefined,
          buckets: [],
          doneBucket: undefined,
          completeWhenDone: false,
          assignedToMe: cfg.assignedToMe ?? true,
        }),
      );
    },
    onSelectPlan: (planId?: string) => {
      const selectedId = String(planId || NONE_OPTION);
      updateConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => ({
          ...cfg,
          plan:
            selectedId === NONE_OPTION
              ? undefined
              : plans.find((plan) => plan?.id === selectedId) || undefined,
          buckets: [],
          doneBucket: undefined,
          completeWhenDone: false,
        }),
      );
    },
    onSelectBuckets: (bucketIds: string[]) => {
      const selected = buckets.filter((bucket) =>
        bucketIds.includes(bucket.id),
      );

      updateConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => {
          const nextDoneBucket =
            cfg.doneBucket &&
            selected.some((bucket) => bucket.id === cfg.doneBucket?.id)
              ? undefined
              : cfg.doneBucket;

          return {
            ...cfg,
            buckets: selected,
            doneBucket: nextDoneBucket,
            completeWhenDone: nextDoneBucket ? cfg.completeWhenDone : false,
          };
        },
      );
    },
    onSelectDoneBucket: (bucketId?: string) => {
      const selectedId = String(bucketId || NONE_OPTION);
      const selectedBucket = buckets.find((bucket) => bucket.id === selectedId);

      updateConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => ({
          ...cfg,
          doneBucket: selectedId === NONE_OPTION ? undefined : selectedBucket,
          buckets: (cfg.buckets ?? []).filter(
            (bucket) => bucket.id !== selectedId,
          ),
          completeWhenDone:
            selectedId === NONE_OPTION ? false : Boolean(cfg.completeWhenDone),
        }),
      );
    },
    onToggleAssignedToMe: (value: boolean) => {
      updateConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => ({
          ...cfg,
          assignedToMe: value,
        }),
      );
    },
    onToggleCompleteWhenDone: (value: boolean) => {
      updateConnectionConfiguration<PlannerConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => ({
          ...cfg,
          completeWhenDone: value,
        }),
      );
    },
  };
};
